import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Message } from "rsuite";

import constants from "utils/constants";

import useRefreshPage from "hooks/useRefreshPage";

import {
    EnrollStep,
    StepStatus,
    StepType,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";

import ErrorSectionContainer from "components/Common/ErrorSection/ErrorSectionContainer";
import SelectCustomerContainer from "./SelectCustomer/SelectCustomerContainer";
import IntegrationInformationContainer from "./IntegrationInformation/IntegrationInformationContainer";
import AutomatedStepContainer from "../AutomatedStep/AutomatedStepContainer";
import SetAdmin from "./SetAdmin/SetAdmin";
import GuestTenantsStepContainer from "./GuestTenants/GuestTenantsStepContainer";
import DataImport from "./DataImport/DataImport";
import Settings from "./Settings/Settings";
import Companies from "./Companies/Companies";

import styles from "./StepFrame.module.css";

interface IComponentProps {
    runningStepIndex: number;
    activeStep: EnrollStep | null;
    errorMessage: string;
    errorEnrollManualStepResponseMessage: string;
    isActiveStepApproved: boolean;
    isActiveStepRetried: boolean;
    environmentMoniker: string | null;
    enrollId: string;
    setIsStepFormValid: Dispatch<SetStateAction<() => boolean>>;
    fetchEnrollWorkflow: (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => void;
}

const StepFrame: React.FC<IComponentProps> = ({
    activeStep,
    errorMessage,
    errorEnrollManualStepResponseMessage,
    isActiveStepApproved,
    isActiveStepRetried,
    runningStepIndex,
    environmentMoniker,
    enrollId,
    setIsStepFormValid,
    fetchEnrollWorkflow,
}: IComponentProps) => {
    const { t } = useTranslation();

    const [isInfoMessageShown, setIsInfoMessageShown] =
        React.useState<boolean>(false);

    const activeStepIndex = activeStep?.order || 0;

    const isStepAutomated = activeStep?.type === StepType.Automated;
    const isStepFailed = activeStep?.status === StepStatus.Failed;
    const isStepSkipped = activeStep?.status === StepStatus.Skipped;

    const isActiveStepErrorMessageVisible =
        errorMessage.length > 0 && isStepFailed;

    const isManualStepErrorResponseMessageVisible =
        errorEnrollManualStepResponseMessage.length > 0 &&
        !isStepFailed &&
        activeStepIndex === runningStepIndex;

    useRefreshPage(() => {
        if (environmentMoniker && enrollId) {
            fetchEnrollWorkflow(environmentMoniker, enrollId);
        }
    });

    useEffect(() => {
        isActiveStepRetried
            ? setIsInfoMessageShown(true)
            : setIsInfoMessageShown(false);
    }, [isActiveStepRetried]);

    const activeStepContainer = (stepIndex: number) => {
        switch (stepIndex) {
            case constants.enroll.STEP_1:
                return (
                    <SelectCustomerContainer
                        setIsStepFormValid={setIsStepFormValid}
                    />
                );
            case constants.enroll.STEP_2:
                return <GuestTenantsStepContainer />;
            case constants.enroll.STEP_3:
                return (
                    <IntegrationInformationContainer
                        setIsStepFormValid={setIsStepFormValid}
                    />
                );
            case constants.enroll.STEP_4:
            case constants.enroll.STEP_5:
            case constants.enroll.STEP_7:
            case constants.enroll.STEP_8:
            case constants.enroll.STEP_9:
            case constants.enroll.STEP_10:
            case constants.enroll.STEP_11:
                return <AutomatedStepContainer />;

            case constants.enroll.STEP_6:
                return <SetAdmin setIsStepFormValid={setIsStepFormValid} />;

            case constants.enroll.STEP_12:
                return <DataImport setIsStepFormValid={setIsStepFormValid} />;

            case constants.enroll.STEP_13:
                return <Settings setIsStepFormValid={setIsStepFormValid} />;

            case constants.enroll.STEP_14:
                return <Companies />;

            default:
                return null;
        }
    };

    const getFrameBody = () => {
        if (!activeStep) {
            return (
                <Message showIcon type="info" title={t("error.possibleFix")}>
                    {t("enrollTenant.stepFrame.dataRetrieveMessage")}
                </Message>
            );
        } else {
            if (isManualStepErrorResponseMessageVisible) {
                return (
                    <div className={styles.errorSection}>
                        <ErrorSectionContainer isTitleVisible={false} />
                    </div>
                );
            } else if (!isActiveStepErrorMessageVisible || isStepSkipped) {
                if (
                    isActiveStepApproved &&
                    runningStepIndex >= 0 &&
                    runningStepIndex === activeStepIndex
                ) {
                    return (
                        <Message
                            showIcon
                            type="info"
                            title={t("error.possibleFix")}
                        >
                            {t("enrollTenant.stepFrame.stepIsCompletedMessage")}
                        </Message>
                    );
                } else {
                    return activeStepContainer(activeStepIndex);
                }
            } else if (isInfoMessageShown) {
                return (
                    <Message
                        showIcon
                        type="info"
                        title={t("error.possibleFix")}
                    >
                        {t("enrollTenant.stepFrame.retryStepPendingMessage")}
                    </Message>
                );
            } else {
                return isStepAutomated ? (
                    <>{activeStepContainer(activeStepIndex)}</>
                ) : (
                    <div className={styles.errorSection}>
                        <ErrorSectionContainer
                            isTitleVisible={false}
                            helpMessage={t("error.retryFix")}
                        />
                    </div>
                );
            }
        }
    };

    return <div className={styles.currentStep}>{getFrameBody()}</div>;
};

export default StepFrame;
